import axios from 'axios';
import { Baseurl } from '../Api/Baseurl';
import { API_ENDPOINTS } from '../types/dnd.types';

class ReorderService {
    constructor() {
        this.baseURL = Baseurl;
        this.token = localStorage.getItem('medtoken');
    }

    // Get authorization headers
    getHeaders() {
        return {
            Authorization: `Bearer ${this.token}`
        };
    }

    // Update token if needed
    updateToken(newToken) {
        this.token = newToken;
        localStorage.setItem('medtoken', newToken);
    }

    // Single entity reorder
    async reorderEntity(entityType, entityId, newPosition, scopeType = null, scopeId = null, switchWithId = null) {
        try {
            const payload = {
                entityType,
                entityId
            };

            // Support both regular reordering and position switching
            if (switchWithId) {
                payload.switchWithId = switchWithId;
            } else if (newPosition !== undefined && newPosition !== null) {
                payload.newPosition = newPosition;
            } else {
                throw new Error('Either newPosition or switchWithId must be provided');
            }

            if (scopeType && scopeId) {
                payload.scopeType = scopeType;
                payload.scopeId = scopeId;
            }

            const response = await axios.post(
                `${this.baseURL}${API_ENDPOINTS.REORDER}`,
                payload,
                { headers: this.getHeaders() }
            );

            return {
                success: true,
                data: response.data,
                message: response.data?.msg || `${entityType} reordered successfully`
            };
        } catch (error) {
            console.error('Error reordering entity:', error);
            return {
                success: false,
                error: error.response?.data?.msg || error.message || 'Failed to reorder entity',
                message: error.response?.data?.msg || error.message || 'Failed to reorder entity'
            };
        }
    }

    // Switch positions between two entities
    async switchPositions(entityType, firstEntityId, secondEntityId, scopeType = null, scopeId = null) {
        try {
            return await this.reorderEntity(
                entityType,
                firstEntityId,
                null,
                scopeType,
                scopeId,
                secondEntityId
            );
        } catch (error) {
            console.error('Error switching positions:', error);
            return {
                success: false,
                error: error.message || 'Failed to switch positions',
                message: error.message || 'Failed to switch positions'
            };
        }
    }

    // Transfer entity between containers
    async transferEntity(entityType, entityId, targetType, targetId) {
        try {
            const response = await axios.post(
                `${this.baseURL}${API_ENDPOINTS.TRANSFER}`,
                {
                    entityType,
                    entityId,
                    targetType,
                    targetId
                },
                { headers: this.getHeaders() }
            );

            return {
                success: true,
                data: response.data,
                message: response.data?.msg || `${entityType} transferred successfully`
            };
        } catch (error) {
            console.error('Error transferring entity:', error);
            return {
                success: false,
                error: error.response?.data?.msg || error.message || 'Failed to transfer entity',
                message: error.response?.data?.msg || error.message || 'Failed to transfer entity'
            };
        }
    }

    // Bulk reorder multiple entities
    async bulkReorderEntities(entityType, reorderData, scopeType = null, scopeId = null) {
        try {
            const payload = {
                entityType,
                reorderData
            };

            if (scopeType && scopeId) {
                payload.scopeType = scopeType;
                payload.scopeId = scopeId;
            }

            const response = await axios.post(
                `${this.baseURL}${API_ENDPOINTS.BULK_REORDER}`,
                payload,
                { headers: this.getHeaders() }
            );

            return {
                success: true,
                data: response.data,
                message: response.data?.msg || `${entityType} entities reordered successfully`
            };
        } catch (error) {
            console.error('Error bulk reordering entities:', error);
            return {
                success: false,
                error: error.response?.data?.msg || error.message || 'Failed to bulk reorder entities',
                message: error.response?.data?.msg || error.message || 'Failed to bulk reorder entities'
            };
        }
    }

    // Move entity to end of list
    async moveToEnd(entityType, entityId, scopeType = null, scopeId = null) {
        try {
            const payload = {
                entityType,
                entityId
            };

            if (scopeType && scopeId) {
                payload.scopeType = scopeType;
                payload.scopeId = scopeId;
            }

            const response = await axios.post(
                `${this.baseURL}${API_ENDPOINTS.MOVE_TO_END}`,
                payload,
                { headers: this.getHeaders() }
            );

            return {
                success: true,
                data: response.data,
                message: response.data?.msg || `${entityType} moved to end successfully`
            };
        } catch (error) {
            console.error('Error moving entity to end:', error);
            return {
                success: false,
                error: error.response?.data?.msg || error.message || 'Failed to move entity to end',
                message: error.response?.data?.msg || error.message || 'Failed to move entity to end'
            };
        }
    }

    // Move entity to beginning of list
    async moveToBeginning(entityType, entityId, scopeType = null, scopeId = null) {
        try {
            const payload = {
                entityType,
                entityId
            };

            if (scopeType && scopeId) {
                payload.scopeType = scopeType;
                payload.scopeId = scopeId;
            }

            const response = await axios.post(
                `${this.baseURL}${API_ENDPOINTS.MOVE_TO_BEGINNING}`,
                payload,
                { headers: this.getHeaders() }
            );

            return {
                success: true,
                data: response.data,
                message: response.data?.msg || `${entityType} moved to beginning successfully`
            };
        } catch (error) {
            console.error('Error moving entity to beginning:', error);
            return {
                success: false,
                error: error.response?.data?.msg || error.message || 'Failed to move entity to beginning',
                message: error.response?.data?.msg || error.message || 'Failed to move entity to beginning'
            };
        }
    }

    // Fix position gaps
    async fixPositionGaps(entityType, scopeType = null, scopeId = null) {
        try {
            const payload = {
                entityType
            };

            if (scopeType && scopeId) {
                payload.scopeType = scopeType;
                payload.scopeId = scopeId;
            }

            const response = await axios.post(
                `${this.baseURL}${API_ENDPOINTS.FIX_GAPS}`,
                payload,
                { headers: this.getHeaders() }
            );

            return {
                success: true,
                data: response.data,
                message: response.data?.msg || `${entityType} position gaps fixed successfully`
            };
        } catch (error) {
            console.error('Error fixing position gaps:', error);
            return {
                success: false,
                error: error.response?.data?.msg || error.message || 'Failed to fix position gaps',
                message: error.response?.data?.msg || error.message || 'Failed to fix position gaps'
            };
        }
    }

    // Get position statistics
    async getPositionStats(entityType, scopeType = null, scopeId = null) {
        try {
            const params = new URLSearchParams({ entityType });
            
            if (scopeType && scopeId) {
                params.append('scopeType', scopeType);
                params.append('scopeId', scopeId);
            }

            const response = await axios.get(
                `${this.baseURL}${API_ENDPOINTS.POSITION_STATS}?${params}`,
                { headers: this.getHeaders() }
            );

            return {
                success: true,
                data: response.data,
                message: response.data?.msg || `${entityType} position statistics retrieved successfully`
            };
        } catch (error) {
            console.error('Error getting position statistics:', error);
            return {
                success: false,
                error: error.response?.data?.msg || error.message || 'Failed to get position statistics',
                message: error.response?.data?.msg || error.message || 'Failed to get position statistics'
            };
        }
    }
}


const reorderService = new ReorderService();

export default reorderService;
